import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, RefreshCw, X, Calendar, AlertTriangle, Clock, CheckCircle2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const URGENCY_STYLE = {
  overdue:  { dot: 'bg-red-500',   badge: 'bg-red-900/60 text-red-300 border-red-700',   label: 'Overdue' },
  due_soon: { dot: 'bg-amber-400', badge: 'bg-amber-900/60 text-amber-300 border-amber-700', label: 'Due Soon' },
  upcoming: { dot: 'bg-green-500', badge: 'bg-green-900/40 text-green-300 border-green-700', label: 'Upcoming' },
};

const STATUS_COLOR = {
  'Not Accepted': 'text-zinc-400',
  'Accepted':     'text-blue-400',
  'In Production':'text-amber-400',
  'Packing':      'text-purple-400',
  'Dispatched':   'text-green-400',
};

export default function POCalendar() {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/sales/calendar`, { withCredentials: true });
      setOrders(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Calendar grid helpers
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // Group orders by due_date_str
  const ordersByDate = {};
  orders.forEach(o => {
    const d = o.due_date_str;
    if (!ordersByDate[d]) ordersByDate[d] = [];
    ordersByDate[d].push(o);
  });

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));

  // Summary stats
  const overdue = orders.filter(o => o.urgency === 'overdue').length;
  const dueSoon = orders.filter(o => o.urgency === 'due_soon').length;
  const upcoming = orders.filter(o => o.urgency === 'upcoming').length;

  // Build calendar cells
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="space-y-5" data-testid="po-calendar-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-zinc-50 flex items-center gap-2">
            <Calendar size={22} className="text-amber-400" />
            Sales Dispatch Calendar
          </h1>
          <p className="text-zinc-500 text-sm mt-0.5">Track dispatch deadlines and delivery status</p>
        </div>
        <button onClick={fetchOrders} disabled={loading}
          className="flex items-center gap-1.5 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-xl text-zinc-400 hover:text-zinc-200 text-sm transition-all">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Overdue', value: overdue, icon: AlertTriangle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-800' },
          { label: 'Due Soon (0–3 days)', value: dueSoon, icon: Clock, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-800' },
          { label: 'Upcoming', value: upcoming, icon: CheckCircle2, color: 'text-green-400', bg: 'bg-green-500/10 border-green-800' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className={`rounded-xl p-4 border flex items-center gap-3 ${bg}`}>
            <Icon size={18} className={color} />
            <div>
              <div className="text-zinc-400 text-xs">{label}</div>
              <div className={`font-heading font-bold text-2xl ${color}`}>{value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Calendar */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        {/* Month Nav */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors">
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-3">
            <h2 className="font-heading font-semibold text-zinc-50 text-base">
              {MONTH_NAMES[month]} {year}
            </h2>
            <button onClick={goToday}
              className="text-xs text-amber-400 border border-amber-500/30 px-2 py-0.5 rounded-lg hover:bg-amber-500/10 transition-colors">
              Today
            </button>
          </div>
          <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors">
            <ChevronRight size={16} />
          </button>
        </div>

        {/* Day headers */}
        <div className="grid grid-cols-7 border-b border-zinc-800">
          {DAY_NAMES.map(d => (
            <div key={d} className="text-center text-zinc-600 text-xs font-medium py-2">{d}</div>
          ))}
        </div>

        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (!day) return <div key={`empty-${idx}`} className="min-h-[100px] border-b border-r border-zinc-800/50 bg-zinc-950/30" />;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayOrders = ordersByDate[dateStr] || [];
            const isToday = dateStr === todayStr;
            return (
              <div key={day} className={`min-h-[100px] border-b border-r border-zinc-800/50 p-1.5 transition-colors ${isToday ? 'bg-amber-500/5 border-amber-500/20' : 'hover:bg-zinc-800/20'}`}>
                <div className={`text-xs font-semibold mb-1 w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-amber-500 text-black' : 'text-zinc-500'}`}>
                  {day}
                </div>
                <div className="space-y-0.5">
                  {dayOrders.slice(0, 3).map(order => {
                    const u = URGENCY_STYLE[order.urgency] || URGENCY_STYLE.upcoming;
                    const kindDot = order.is_overdue || order.urgency === 'overdue'
                      ? 'bg-red-500'
                      : order.kind === 'production' ? 'bg-green-500' : 'bg-amber-400';
                    const kindBorder = order.kind === 'production' ? 'border-l-2 border-l-green-500' : 'border-l-2 border-l-amber-400';
                    return (
                      <button
                        key={`${order.id}-${order.kind || 'x'}-${order.due_date_str}`}
                        onClick={() => setSelectedOrder(order)}
                        className={`w-full text-left px-1.5 py-0.5 rounded text-[10px] border truncate transition-all hover:opacity-80 ${u.badge} ${kindBorder}`}
                        data-testid={`cal-order-${order.id}-${order.kind || 'x'}`}
                        title={`${order.kind === 'production' ? 'Production Due' : 'Dispatch Due'} · ${order.client}`}
                      >
                        <span className={`inline-block w-1.5 h-1.5 rounded-full ${kindDot} mr-1`} />
                        {order.client}
                      </button>
                    );
                  })}
                  {dayOrders.length > 3 && (
                    <div className="text-[10px] text-zinc-500 pl-1">+{dayOrders.length - 3} more</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
        <span className="font-medium">Kind:</span>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500" />Production Due</div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-amber-400" />Dispatch Due</div>
        <div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-red-500" />Overdue</div>
        <span className="font-medium ml-4">Urgency:</span>
        {Object.entries(URGENCY_STYLE).map(([key, s]) => (
          <div key={key} className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${s.dot}`} />
            {s.label}
          </div>
        ))}
      </div>

      {/* Order Detail Panel */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setSelectedOrder(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-sm p-5" onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-heading font-semibold text-zinc-50">{selectedOrder.client}</div>
                <div className="text-zinc-500 text-xs mt-0.5 font-mono">{selectedOrder.id}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-0.5 rounded-full border ${URGENCY_STYLE[selectedOrder.urgency]?.badge}`}>
                  {URGENCY_STYLE[selectedOrder.urgency]?.label}
                </span>
                <button onClick={() => setSelectedOrder(null)} className="text-zinc-500 hover:text-zinc-200">
                  <X size={15} />
                </button>
              </div>
            </div>
            <div className="space-y-2.5 text-sm">
              {[
                ['Product', selectedOrder.product_name],
                ['Quantity', `${selectedOrder.quantity} ${selectedOrder.unit || 'kg'}`],
                ['Total', fmt(selectedOrder.total_amount)],
                ['Status', selectedOrder.status],
                ['Due Date', selectedOrder.due_date_str],
                ['Days', selectedOrder.days_left < 0 ? `${Math.abs(selectedOrder.days_left)} days overdue` : `${selectedOrder.days_left} days remaining`],
                ['Payment', selectedOrder.payment_status || 'Unpaid'],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between">
                  <span className="text-zinc-500 text-xs">{label}</span>
                  <span className={`text-xs font-medium ${label === 'Status' ? STATUS_COLOR[value] || 'text-zinc-300' : label === 'Days' && selectedOrder.days_left < 0 ? 'text-red-400' : 'text-zinc-200'}`}>
                    {value}
                  </span>
                </div>
              ))}
            </div>
            {selectedOrder.notes && (
              <div className="mt-3 pt-3 border-t border-zinc-800">
                <div className="text-zinc-600 text-xs">{selectedOrder.notes}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-10">
          <RefreshCw size={20} className="animate-spin text-zinc-500" />
        </div>
      )}
    </div>
  );
}
